<template>
  <div class="dp02 p-3 mt-3">
    <div class="text-center w-100 mb-3">
      <h2>{{ $t("CreateEditGeneCombination.Header") }}</h2>
    </div>
    <div
      v-if="fetched && availableGeneCombinations.length > 0"
      class="text-center w-100">
      <h3>{{ $t("CreateEditGeneCombination.ChooseExisting") }}</h3>
      <v-multiselect
        v-model="geneCombination"
        track-by="id"
        :options="availableGeneCombinations"
        @input="setGeneCombination(geneCombination), $emit('next-step')">
        <template slot="singleLabel"
                  slot-scope="props">
          <div class="option__desc">
            <h5 class="option__title">
              {{ props.option.phenotype }}
            </h5>
            <span class="option__small">
              {{ props.option.name }}
            </span>
          </div>
        </template>
        <template slot="option"
                  slot-scope="props">
          <div class="option__desc">
            <h5 class="option__title">
              {{ props.option.phenotype }}
            </h5>
            <span class="option__small">
              {{ props.option.name }}
            </span>
          </div>
        </template>
      </v-multiselect>
    </div>
    <div class="text-center w-100 mt-3">
      <h3 v-if="fetched">
        {{ $t("CreateEditGeneCombination.Fetched") }} (,)
      </h3>
      <h3 v-else>
        {{ $t("CreateEditGeneCombination.NotFetched") }}
        (,)
      </h3>
      <ValidationObserver ref="observer"
                          v-slot="{invalid}"
                          tag="form"
                          @submit.prevent="submit()">
        <div class="row mt-3">
          <div class="col-lg-6">
            <ValidationProvider
              name="gene combination"
              rules="required|geneCombinationGenesUnique"
              v-slot="{errors, classes}">
              <b-form-textarea
                v-model="geneCombinationInput"
                :class="{...classes, myInput: true}"
                placeholder="Combination"
                no-resize
                rows="7" />
              <p class="myInputError">
                {{ errors[0] }}
              </p>
            </ValidationProvider>
          </div>
          <div class="col-lg-6">
            <div class="row">
              <div class="col-lg-3">
                <label for="phenotype">{{ $t("common.Phenotype") }}</label>
              </div>
              <div class="col-lg-9">
                <ValidationProvider
                  name="phenotype"
                  rules="required"
                  v-slot="{errors, classes}">
                  <b-form-input
                    id="phenotype"
                    v-model="phenotype"
                    :class="{...classes, myInput: true}" />
                  <p class="myInputError">
                    {{ errors[0] }}
                  </p>
                </ValidationProvider>
              </div>
            </div>
            <div class="row mt-3">
              <div class="col-lg-3">
                <label for="gender">{{ $t("common.Gender") }}</label>
              </div>
              <div class="col-lg-9">
                <GenderInput id="gender"
                             v-model="gender" />
              </div>
            </div>
          </div>
          <template v-if="serverValidationErrors.length !== 0">
            <div class="p-3">
              <p class="errorMsg"
                 v-for="(error, i) in serverValidationErrors"
                 :key="i">
                {{ error }}
              </p>
            </div>
          </template>
        </div>
        <b-button
          class="myButton"
          type="submit">
          {{ $t("common.NextStep") }}
        </b-button>
      </ValidationObserver>
    </div>
  </div>
</template>
<script>
import { mapActions, mapMutations, mapGetters } from 'vuex';
import GenderInput from '@components/input/GenderInput';

export default {
  name: 'CreateEditGeneCombination',
  components: { GenderInput },
  data() {
    return {
      geneCombination: '',
      geneCombinationInput: '',
      fetched: true,
      phenotype: '',
      serverValidationErrors: []
    };
  },
  computed: {
    availableGeneCombinations() {
      return this.getGeneCombinations();
    },
    gender: {
      get() {
        return this.getGender()();
      },
      set(val) {
        return this.setGender(val);
      }
    }
  },
  watch: {
    geneCombination(val) {
      this.setGeneCombination(val);
    }
  },
  mounted() {
    if (this.getSubCategory().id != null && this.getSubCategory().id !== 0) {
      this.fetchGeneCombinations(this.getSubCategory().id);
    } else if (this.getCategory().id != null && this.getSubCategory().name === 'None') {
      this.fetchGeneCombinations(this.getCategory().id);
    } else {
      this.fetched = false;
    }

    if (this.getGeneCombination().id === 0) {
      this.geneCombinationInput = this.getGeneCombination().name;
      this.phenotype = this.getPhenotype();
      this.gender = this.getGender()();
    } else {
      this.geneCombination = this.getGeneCombination();
    }
  },
  methods: {
    ...mapActions({
      fetchGeneCombinations: 'createTemplate/fetchGeneCombinations',
      validateGeneCombination: 'createTemplate/validateGeneCombination'
    }),
    ...mapMutations({
      setGeneCombination: 'createTemplate/setGeneCombination',
      setGender: 'createTemplate/setGender',
      setPhenotype: 'createTemplate/setPhenotype',
      setGenetypeCombinations: 'createTemplate/setGenetypeCombinations'
    }),
    ...mapGetters({
      getGeneCombinations: 'createTemplate/getGeneCombinations',
      getCategory: 'createTemplate/getCategory',
      getSubCategory: 'createTemplate/getSubCategory',
      getGeneCombination: 'createTemplate/getGeneCombination',
      getPhenotype: 'createTemplate/getPhenotype',
      getGender: 'createTemplate/getGender'
    }),
    setValue(val) {
      this.subCategorySearch = val;
    },
    async submit() {
      this.geneCombinationInput = this.geneCombinationInput.trim();
      if (this.geneCombinationInput.slice(-1) === ',') {
        this.geneCombinationInput = this.geneCombinationInput.slice(0, -1);
      }
      const isValid = await this.$refs.observer.validate();
      if (isValid) {
        this.setGenetypeCombinations([]);
        this.setGeneCombination({ id: 0, name: this.geneCombinationInput });
        this.setGender(this.gender);
        this.setPhenotype(this.phenotype);

        this.serverValidationErrors = [];
        this.validateGeneCombination()
          .then((response) => {
            const validationResult = response.data;
            if (validationResult.isValid) {
              this.$emit('next-step');
            } else {
              this.serverValidationErrors = validationResult.errors;
            }
          });
      }
    }
  }
};
</script>
<style lang="scss" scoped>

</style>
